import React, { useMemo, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Pagination,
  InputGroup,
  Form
} from 'react-bootstrap';

const generateSampleData = (count = 40) => {
  const statuses = ['Success', 'Pending', 'Failed'];
  const txnModes = ['IMPS', 'NEFT', 'RTGS', 'UPI'];
  const messages = [
    'Payout settled successfully.',
    'Awaiting bank confirmation.',
    'Transaction failed, please retry.',
    'Settlement initiated.',
    'Processing delayed by bank.'
  ];

  return Array.from({ length: count }, (_, index) => {
    const amount = +(Math.random() * 9000 + 500).toFixed(2);
    const balance = +(Math.random() * 50000 + 2000).toFixed(2);
    const commission = +(Math.random() * 120 + 10).toFixed(2);
    const tds = +(commission * 0.05).toFixed(2);
    const settlementAmount = +(amount - commission - tds).toFixed(2);

    return {
      id: `PAY${String(index + 1).padStart(5, '0')}`,
      date: new Date(
        2024,
        Math.floor(Math.random() * 12),
        Math.floor(Math.random() * 28) + 1
      )
        .toISOString()
        .split('T')[0],
      mobile: `9${Math.floor(Math.random() * 1_000_000_000)
        .toString()
        .padStart(9, '0')}`,
      status: statuses[Math.floor(Math.random() * statuses.length)],
      amount,
      balance,
      commission,
      tds,
      settlementAmount,
      bankRrn: `RRN${Math.floor(Math.random() * 10_000_000_000)}`,
      txnMode: txnModes[Math.floor(Math.random() * txnModes.length)],
      orderId: `ORD${Math.floor(Math.random() * 1_000_000_000)}`,
      cardOrUid: `XXXX-${Math.floor(Math.random() * 9000 + 1000)}`,
      message: messages[Math.floor(Math.random() * messages.length)]
    };
  });
};

const formatCurrency = (value) => `₹${Number(value || 0).toFixed(2)}`;

const PayoutReports = () => {
  const [data] = useState(generateSampleData(60));
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const rowsPerPage = 8;

  const filteredData = useMemo(() => {
    if (!searchQuery) {
      return data;
    }

    const query = searchQuery.toLowerCase();

    return data.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(query)
      )
    );
  }, [data, searchQuery]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <Pagination.Item
            key={i}
            active={i === currentPage}
            onClick={() => setCurrentPage(i)}
          >
            {i}
          </Pagination.Item>
        );
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          items.push(
            <Pagination.Item
              key={i}
              active={i === currentPage}
              onClick={() => setCurrentPage(i)}
            >
              {i}
            </Pagination.Item>
          );
        }
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        items.push(
          <Pagination.Item
            key={totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            {totalPages}
          </Pagination.Item>
        );
      } else if (currentPage >= totalPages - 2) {
        items.push(
          <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
            1
          </Pagination.Item>
        );
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        for (let i = totalPages - 3; i <= totalPages; i++) {
          items.push(
            <Pagination.Item
              key={i}
              active={i === currentPage}
              onClick={() => setCurrentPage(i)}
            >
              {i}
            </Pagination.Item>
          );
        }
      } else {
        items.push(
          <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
            1
          </Pagination.Item>
        );
        items.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          items.push(
            <Pagination.Item
              key={i}
              active={i === currentPage}
              onClick={() => setCurrentPage(i)}
            >
              {i}
            </Pagination.Item>
          );
        }
        items.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
        items.push(
          <Pagination.Item
            key={totalPages}
            onClick={() => setCurrentPage(totalPages)}
          >
            {totalPages}
          </Pagination.Item>
        );
      }
    }

    return items;
  };

  return (
    <Container fluid className="payout-reports-page p-4">
      <Row>
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Header
              className="bg-white border-bottom"
              style={{ padding: '16px 24px' }}
            >
              <Row className="align-items-center">
                <Col md={6}>
                  <h5
                    className="mb-0"
                    style={{ fontWeight: 600, color: '#2c2268' }}
                  >
                    Payin Reports
                  </h5>
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                  <InputGroup style={{ maxWidth: '300px' }}>
                    <InputGroup.Text
                      style={{
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #ddd'
                      }}
                    >
                      <i className="bi bi-search"></i>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ border: '1px solid #ddd' }}
                    />
                  </InputGroup>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="mb-0">
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      {[
                        'Created Date',
                        'Mobile No.',
                        'Status',
                        'Amount',
                        'Balance',
                        'Commission',
                        'TDS',
                        'Settlement Amount',
                        'Bank RRN',
                        'Txn Mode',
                        'Order Id',
                        'Card No / UID No',
                        'Message'
                      ].map((column) => (
                        <th
                          key={column}
                          style={{
                            padding: '8px 12px',
                            fontWeight: 600,
                            color: '#555',
                            borderBottom: '2px solid #dee2e6',
                            fontSize: '14px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.length > 0 ? (
                      currentData.map((row) => (
                        <tr
                          key={row.id}
                          style={{ transition: 'background-color 0.2s' }}
                        >
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>
                            {row.date}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#333', fontSize: '13px' }}>
                            {row.mobile}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>
                            {row.status}
                          </td>
                          <td
                            style={{
                              padding: '10px 12px',
                              color: '#333',
                              fontWeight: 600,
                              fontSize: '13px'
                            }}
                          >
                            {formatCurrency(row.amount)}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#333', fontSize: '13px' }}>
                            {formatCurrency(row.balance)}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#333', fontSize: '13px' }}>
                            {formatCurrency(row.commission)}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#333', fontSize: '13px' }}>
                            {formatCurrency(row.tds)}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#333', fontSize: '13px' }}>
                            {formatCurrency(row.settlementAmount)}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>
                            {row.bankRrn}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>
                            {row.txnMode}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>
                            {row.orderId}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#333', fontSize: '13px' }}>
                            {row.cardOrUid}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666', fontSize: '13px' }}>
                            <details className="message-summary">
                              <summary>View</summary>
                              <div className="message-content">{row.message}</div>
                            </details>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={13}
                          className="text-center py-4"
                          style={{ color: '#999' }}
                        >
                          No payout records found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer
              className="bg-white border-top"
              style={{ padding: '12px 24px' }}
            >
              <Row className="align-items-center">
                <Col md={6}>
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    Showing {filteredData.length === 0 ? 0 : indexOfFirstRow + 1}{' '}
                    to {Math.min(indexOfLastRow, filteredData.length)} of{' '}
                    {filteredData.length}
                  </div>
                </Col>
                <Col md={6} className="d-flex justify-content-end">
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {getPaginationItems()}
                    <Pagination.Next
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </Col>
              </Row>
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      <style jsx="true">{`
        .payout-reports-page {
          background-color: #fff;
          min-height: calc(100vh - 100px);
        }

        .table tbody tr:hover {
          background-color: #f8f9fa;
        }

        .pagination .page-link {
          color: #0d6efd;
          border: 1px solid #dee2e6;
          padding: 6px 12px;
          font-size: 14px;
        }

        .pagination .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
          color: white;
        }

        .pagination .page-link:hover {
          background-color: #e7f1ff;
          border-color: #0d6efd;
          color: #0d6efd;
        }

        .pagination .page-item.disabled .page-link {
          color: #6c757d;
          pointer-events: none;
          background-color: #fff;
          border-color: #dee2e6;
        }

        .message-summary {
          margin: 0;
        }

        .message-summary summary {
          font-size: 13px;
          color: #666;
          cursor: pointer;
          list-style: none;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .message-summary summary::-webkit-details-marker {
          display: none;
        }

        .message-summary summary::marker {
          display: none;
        }

        .message-summary[open] summary {
          color: #0d6efd;
        }

        .message-content {
          font-size: 13px;
          color: #444;
          margin-top: 4px;
          line-height: 1.4;
        }

        @media (max-width: 768px) {
          .table-responsive {
            overflow-x: auto;
          }

          .pagination {
            font-size: 12px;
          }
        }
      `}</style>
    </Container>
  );
};

export default PayoutReports;

